import { ref } from 'vue';

import { ProductVariant } from '@/__generated__/storefront/graphql';
import { variantLoyaltyPointsQuery } from '@/shared/graphql/storefront/queries';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useUserStore } from '@/shared/pinia/modules/user';
import { getApolloClients } from '@/shared/utils/apollo-client';
import { shopifyIdToGid } from '@/shared/utils/shopify/id-formatter';

type PointPrice = {
  id: string | number;
  pointsCost: number;
};

export function useCartValidations() {
  const fetchedPointPrices = ref<PointPrice[]>([]);

  const currentPointsInCart = (cartItems: ShopifyCartItem[]) => {
    return cartItems.reduce((pointsInCart, item) => {
      const pointsData = fetchedPointPrices.value.find(
        (pointProduct) => pointProduct.id === item.id
      );

      if (pointsData && pointsData.pointsCost > 0) {
        return pointsInCart + pointsData.pointsCost * item.quantity;
      }

      return pointsInCart;
    }, 0);
  };

  const validateUserIsAuthenticated = () => {
    const userStore = useUserStore();

    if (!userStore.authenticated) {
      throw new Error(
        window.Hobbii?.cartMessages.notAuthenticatedForUsingPoints ||
          'This item can only be purchased with points as a Hobbii member.'
      );
    }
  };

  const isFromPointshop = (
    item: ShopifyCartItem,
    isAddingMainProduct: boolean = false
  ): boolean => {
    if (isAddingMainProduct) {
      const productStore = useProductStore();
      return productStore.isPointProduct;
    } else {
      const hasPointsProperty = !!item.properties?._pointsPrice || false;

      // if the item is free and it is not a pattern, then it is a point product
      const isFreeAndNotPattern =
        item.final_line_price === 0 &&
        !!item.sku &&
        !item.sku.startsWith('pattern-');

      return hasPointsProperty || isFreeAndNotPattern;
    }
  };

  const getPointsUnitPrice = async (
    item: ShopifyCartItem,
    isAddingMainProduct?: boolean
  ): Promise<number> => {
    if (isAddingMainProduct) {
      const productStore = useProductStore();
      return productStore.pointsPrice || 0;
    } else {
      const fetchedPointsPrice = fetchedPointPrices.value.find(
        (p) => p.id === item.id
      );

      const pointsPriceFromProperties = Number(item.properties?._pointsPrice);
      if (!isNaN(pointsPriceFromProperties)) {
        return pointsPriceFromProperties;
      } else if (fetchedPointsPrice) {
        return fetchedPointsPrice.pointsCost;
      } else {
        const pointsPricePerUnit = await fetchPointsPriceForItem(item);
        return pointsPricePerUnit;
      }
    }
  };

  const validateUserHasEnoughPoints = async (
    futureTotalPointsInCart: number
  ) => {
    const userStore = useUserStore();
    const customerPointsBalance = await userStore.getCustomerPointsBalance();

    if (
      futureTotalPointsInCart > 0 &&
      customerPointsBalance != null &&
      customerPointsBalance < futureTotalPointsInCart
    ) {
      throw new Error(
        window.Hobbii?.cartMessages.insufficientPoints ||
          'Almost there! You need to earn some points to be able to buy this item'
      );
    }
  };

  const fetchPointsPriceForItem = async (
    item: ShopifyCartItem
  ): Promise<number> => {
    const { storefront } = await getApolloClients();
    const pointsCostQueryResponse = await storefront.query({
      query: variantLoyaltyPointsQuery,
      variables: {
        id: shopifyIdToGid(item.id.toString(), 'ProductVariant')
      }
    });
    const productVariant = pointsCostQueryResponse.data.node as ProductVariant;

    const pointsCost = Number(productVariant?.metafield?.value) || 0;
    if (pointsCost > 0) {
      fetchedPointPrices.value.push({ id: item.id, pointsCost });
    }

    return pointsCost;
  };

  const checkAndFetchPointsWhenNeeded = async (
    cartItems: ShopifyCartItem[]
  ) => {
    for (const item of cartItems) {
      const isPointProduct = isFromPointshop(item);

      if (isPointProduct) {
        const alreadyCalculated = fetchedPointPrices.value.find(
          (p) => p.id === item.id
        );

        if (!alreadyCalculated) {
          await fetchPointsPriceForItem(item);
        }
      }
    }
  };

  /**
   * Validates that the points products, if exist, can be added to the cart
   * @param payload
   * @returns
   */
  const validateAddPayload = async (
    payload: ShopifyCartAddPayload,
    cartItems: ShopifyCartItem[]
  ) => {
    const isThereAPointProduct = payload.items.some((item) =>
      isFromPointshop(item, true)
    );

    if (isThereAPointProduct) {
      validateUserIsAuthenticated();

      //calculate future points in cart
      const pointPricesToBeAdded: number[] = await Promise.all(
        payload.items.map(async (item: ShopifyCartItem) => {
          const pointsUnitPrice = await getPointsUnitPrice(item, true);

          return pointsUnitPrice * item.quantity;
        })
      );
      const pricesToAdd = pointPricesToBeAdded.reduce(
        (acc, price) => acc + price
      );

      const futurePointsInCart = currentPointsInCart(cartItems) + pricesToAdd;

      await validateUserHasEnoughPoints(futurePointsInCart);
    }

    return true;
  };

  /**
   * validate that the points products, if exist, can be changed in the cart
   */
  const validateChangePayload = async (
    payload: {
      line: number;
      quantity: number;
    },
    cartItems: ShopifyCartItem[]
  ) => {
    // if deleting, no validation is needed
    if (payload.quantity > 0) {
      const itemInCart =
        cartItems && (cartItems[payload.line - 1] as ShopifyCartItem);

      if (itemInCart && isFromPointshop(itemInCart)) {
        validateUserIsAuthenticated();

        //calculate future points in cart
        const itemCurrentPointCost = await getPointsUnitPrice(itemInCart);
        const itemCurrentPointsInCart =
          itemCurrentPointCost * itemInCart.quantity;
        const itemFuturePointsInCart = itemCurrentPointCost * payload.quantity;

        const futureTotalPointsInCart =
          currentPointsInCart(cartItems) +
          itemFuturePointsInCart -
          itemCurrentPointsInCart;

        await validateUserHasEnoughPoints(futureTotalPointsInCart);
      }
    }
  };

  return {
    validateAddPayload,
    validateChangePayload,
    checkAndFetchPointsWhenNeeded
  };
}
